import React, { useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import useTranslations from 'hooks/useTranslations';

import BadgeNode, { getTooltipTitle } from './BadgeNode';
import Connector from './Connector';
import { STATUS_ORDER } from './constants';
import type { DraftStatus, BadgeState, LexTimelineProps } from './types';

export type { DraftStatus, BadgeState, LexTimelineProps };

const TRANSLATION_KEYS: Record<DraftStatus, string> = {
  DRAFT: 'draftManuals.draftState.DRAFT',
  IN_APPROVAL: 'draftManuals.draftState.IN_APPROVAL',
  APPROVED: 'draftManuals.draftState.APPROVED',
  ISSUED: 'draftManuals.draftState.ISSUED',
};

export function getBadgeState(badgeIndex: number, currentIndex: number): BadgeState {
  if (badgeIndex < currentIndex) return 'complete';
  if (badgeIndex === currentIndex) return 'active';
  if (badgeIndex === currentIndex + 1) return 'next';
  return 'pending';
}

const LexTimeline: React.FC<LexTimelineProps> = ({
  currentStatus,
  onStatusChange,
  isLoading = false,
  disabled = false,
  disabledTooltip,
  publishUrl,
}) => {
  const t = useTranslations();
  const [loadingTarget, setLoadingTarget] = useState<DraftStatus | null>(null);

  const currentIndex = STATUS_ORDER.indexOf(currentStatus);

  const resolvedDisabledTooltip =
    disabledTooltip ?? t('draftManuals.draftState.noPublishableChanges');

  const handleBadgeClick = (status: DraftStatus, badgeState: BadgeState) => {
    if (isLoading || disabled) return;
    if (status === 'ISSUED') {
      // Only navigate when ISSUED is the next actionable step (currentStatus === APPROVED)
      if (badgeState === 'next' && publishUrl) {
        window.open(publishUrl, '_self');
      }
      return;
    }
    if (badgeState === 'active') return;

    // Forward navigation: only allow advancing exactly one step at a time
    const targetIndex = STATUS_ORDER.indexOf(status);
    if (targetIndex > currentIndex && targetIndex !== currentIndex + 1) return;

    setLoadingTarget(status);
    onStatusChange(status, () => setLoadingTarget(null), () => setLoadingTarget(null));
  };

  const dimmed = isLoading || disabled;

  const row = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      {STATUS_ORDER.map((status, index) => {
        const badgeState = getBadgeState(index, currentIndex);
        const label = status === 'ISSUED'
          ? (badgeState === 'next'
              ? t('draftManuals.draftState.ISSUED_ACTION')
              : t('draftManuals.draftState.ISSUED'))
          : t(TRANSLATION_KEYS[status]);
        const showSpinner = isLoading && loadingTarget === status;
        const tooltipTitle = getTooltipTitle(badgeState, status === 'ISSUED', t);

        return (
          <React.Fragment key={status}>
            {index > 0 && (
              <Connector solid={index <= currentIndex} dimmed={dimmed} />
            )}
            <BadgeNode
              status={status}
              badgeState={badgeState}
              label={label}
              onClick={() => handleBadgeClick(status, badgeState)}
              dimmed={dimmed}
              disabled={disabled}
              showSpinner={showSpinner}
              tooltipTitle={tooltipTitle}
            />
          </React.Fragment>
        );
      })}
    </Box>
  );

  if (disabled) {
    return (
      <Tooltip title={resolvedDisabledTooltip} placement='bottom'>
        <Box sx={{ display: 'inline-flex' }}>{row}</Box>
      </Tooltip>
    );
  }

  return row;
};

export default LexTimeline;
